import type { FC, ReactNode } from 'react'
import type { Tone } from '@/types'

import { useState } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import { cn } from '@/lib/utils'
import { EASE_OUT_EXPO, Reveal } from '@/components/motion/reveal'
import { RollText } from '@/components/motion/roll-text'
import { ToneSection } from '@/components/generals/tone'
import { mustHaves } from '@/constants/content'

interface MustHavesProps {
    tone?: Tone
    label?: string
}

// The eight must-haves as an index: the title column stays put while the list
// opens one item at a time.
export const MustHaves: FC<MustHavesProps> = ({ tone = 'ink', label = '(06) Must-haves' }): ReactNode => {
    const [open, setOpen] = useState(0)
    const counter = String(open >= 0 ? open + 1 : mustHaves.length).padStart(2, '0')

    return (
        <ToneSection tone={tone} id='must-haves' className='px-5 py-28 md:px-8 lg:px-12 lg:py-40'>
            <div className='grid gap-14 lg:grid-cols-12'>
                <div className='lg:col-span-4'>
                    <div className='flex flex-col gap-8 lg:sticky lg:top-32'>
                        <span className='t-label text-tone-muted'>
                            {label}
                        </span>
                        <Reveal>
                            <h2 className='font-heading t-h2 font-semibold uppercase wdth-75'>
                                Payment fraud prevention must-haves
                            </h2>
                        </Reveal>
                        <div aria-hidden className='hidden items-end gap-3 lg:flex'>
                            <RollText
                                value={counter}
                                className='font-heading text-[clamp(6rem,12vw,12rem)] font-semibold leading-[0.8] tracking-[-0.05em] text-tone-accent wdth-62'
                            />
                            <span className='t-label pb-3 text-tone-muted'>
                                / 08
                            </span>
                        </div>
                    </div>
                </div>

                <ul className='lg:col-span-8'>
                    {mustHaves.map((item, index) => {
                        const expanded = open === index

                        return (
                            <li key={item.title} className='border-t border-tone-line last:border-b'>
                                <h3>
                                    <button
                                        type='button'
                                        aria-expanded={expanded}
                                        aria-controls={`must-have-${index}`}
                                        onClick={() => setOpen(expanded ? -1 : index)}
                                        className='group relative flex w-full items-center gap-5 overflow-hidden py-6 text-left md:gap-8 md:py-7'
                                    >
                                        <span className='absolute inset-0 origin-bottom scale-y-0 bg-tone-surface transition-transform duration-500 ease-out-expo group-hover:scale-y-100' aria-hidden />
                                        <span className='relative t-label w-8 shrink-0 text-tone-muted tabular-nums'>
                                            {String(index + 1).padStart(2, '0')}
                                        </span>
                                        <span className={cn('relative flex-1 font-heading text-[clamp(1.35rem,2.6vw,2.4rem)] font-semibold leading-tight tracking-tight transition-colors duration-500', expanded ? 'text-tone' : 'text-tone/70 group-hover:text-tone')}>
                                            {item.title}
                                        </span>
                                        <span className='relative mr-1 flex size-6 shrink-0 items-center justify-center' aria-hidden>
                                            <span className='absolute h-px w-full bg-current' />
                                            <span className={cn('absolute h-full w-px bg-current transition-transform duration-500 ease-out-expo', expanded && 'rotate-90 scale-y-0')} />
                                        </span>
                                    </button>
                                </h3>

                                <AnimatePresence initial={false}>
                                    {expanded && (
                                        <motion.div
                                            id={`must-have-${index}`}
                                            className='overflow-hidden'
                                            initial={{ height: 0, opacity: 0 }}
                                            animate={{ height: 'auto', opacity: 1 }}
                                            exit={{ height: 0, opacity: 0 }}
                                            transition={{ duration: 0.6, ease: EASE_OUT_EXPO }}
                                        >
                                            <p className='t-lead max-w-2xl pb-8 pl-13 text-tone-muted md:pl-16'>
                                                {item.text}
                                            </p>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </li>
                        )
                    })}
                </ul>
            </div>
        </ToneSection>
    )
}
